import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import z from "zod";
import { useSelector } from "react-redux";

import API_ROUTES from "~/api-routes";
import apiService from "~/services/service";
import useDateTime from "../../../hooks/useDateTime";
import { REQUEST_TYPES } from "../useRequest";

const useRequestDashboard = (type, requestType) => {
  const { id } = useParams();
  const { formatDateTime } = useDateTime();
  const hasFetched = useRef(false);
  const [requestdata, setRequestData] = useState({});
  const [requestLoading, setReuestLoading] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [deletemessageLoading, setdeleteMessageLoading] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [selectedMessage, setSelectedMessage] = useState({});
  const [deleteModal, setDeleteModal] = useState(false);
  const [messages, setMessages] = useState([]);

  // Simple pagination
  const [page, setPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isDataFetching, setDataFetching] = useState(false);

  const PAGE_SIZE = 4;

  const {
    firstName,
    lastName,
    email,
    role,
    id: userID,
  } = useSelector((state) => state.login);

  useEffect(() => {
    fetchARequest();
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      if (type === "coversation") {
        setMessageLoading(true);
        await fetchCoversations(requestType);
        setMessageLoading(false);
        hasFetched.current = true;
      }
    };
    fetchData();
  }, [id]);

  const fetchARequest = async () => {
    setReuestLoading(true);
    const { data } = await apiService(API_ROUTES.FETCH_REQUEST, "GET", {
      replace: {
        id: id,
      },
      schema: z.object({
        id: z.string().uuid(),
      }),
    });

    setReuestLoading(false);
    setRequestData(data);
  };

  const fetchCoversations = async (requestType, skipCache = false) => {
    try {
      setDataFetching(true);
      if (page === 1) {
        setMessageLoading(true);
      }

      const response = await apiService(API_ROUTES.FETCH_CONVERSATIONS, "GET", {
        replace: {
          relatedId: id,
          relatedType:
            requestType === REQUEST_TYPES.AD_HOC ? "REQUEST" : "ESCALATION",
          isPublic: "",
          page: page,
        },
        schema: z.object({
          relatedId: z.string().uuid(),
          relatedType: z.string(),
        }),
        skipCache,
      });

      if (response?.data?.length > 0) {
        setMessages(response.data);

        // Increment page for next fetch
        setPage((prev) => prev + 1);

        // Check if we have more data
        if (response.data.length < PAGE_SIZE) {
          setHasMoreData(false);
        }
      } else {
        setHasMoreData(false);
      }

      setMessageLoading(false);
      setDataFetching(false);
      return response;
    } catch (error) {
      setMessageLoading(false);
      setDataFetching(false);
      return { data: [] };
    }
  };

  const resetPaginationState = () => {
    // Ensure UI shows skeleton instead of empty state during refresh
    setMessageLoading(true);
    setPage(1);
    setMessages([]);
    setHasMoreData(true);
    setDataFetching(false);
  };

  const deleteConversation = async (id) => {
    setdeleteMessageLoading(true);
    await apiService(API_ROUTES.DELETE_CONVERSATION, "DELETE", {
      replace: { id, isPublic: false },
      schema: z.object({
        id: z.string().uuid(),
      }),
    });
    setNotificationMessage(
      "Your message and document has been deleted from this conversation",
    );
    setdeleteMessageLoading(false);
    setDeleteModal(false);
    setSelectedMessage(null);

    // Reset and reload
    resetPaginationState();
    await fetchCoversations(requestType, true); // GXM-1769: skipCache after delete
  };

  const formatDate = (dateString) => {
    let formattedNewDate = formatDateTime({
      date: dateString,
      dateformat: "MMMM do, yyyy 'at' H:mm",
    });
    return formattedNewDate;
  };

  return {
    fetchCoversations,
    deleteConversation,
    setNotificationMessage,
    setDeleteModal,
    setSelectedMessage,
    setdeleteMessageLoading,
    formatDate,
    resetPaginationState,
    states: {
      requestdata,
      requestLoading,
      messageLoading,
      messages,
      deletemessageLoading,
      notificationMessage,
      selectedMessage,
      deleteModal,
      hasMoreData,
      isDataFetching,
    },
  };
};

export default useRequestDashboard;
